import React, { createContext, useContext } from 'react';
import { useSession } from './SessionContext';

const PermissionsContext = createContext();

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { permissions, isAdmin, isSuperAdmin, loading, hasPermission: sessionHasPermission } = useSession();

  const hasPermission = (permission) => {
    return sessionHasPermission(permission);
  };

  const hasAnyPermission = (permissionList) => {
    if (isAdmin) return true;
    return permissionList.some(perm => permissions.includes(perm));
  };

  const hasAllPermissions = (permissionList) => {
    if (isAdmin) return true;
    return permissionList.every(perm => permissions.includes(perm));
  };

  // Common permission checks
  const canViewTeams = () => hasPermission('VIEW_TEAMS');
  const canManageTeams = () => hasPermission('MANAGE_TEAMS');
  const canViewBurstModes = () => hasPermission('VIEW_BURST_MODES');
  const canManageBurstModes = () => hasPermission('MANAGE_BURST_MODES');
  const canViewSettings = () => hasPermission('VIEW_SETTINGS');
  const canManageSettings = () => hasPermission('MANAGE_SETTINGS');
  const canViewOrganization = () => hasPermission('VIEW_ORGANIZATION');
  const canManageOrganization = () => hasPermission('MANAGE_ORGANIZATION');

  const value = {
    permissions,
    isAdmin,
    isSuperAdmin,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canViewTeams,
    canManageTeams,
    canViewBurstModes,
    canManageBurstModes,
    canViewSettings,
    canManageSettings,
    canViewOrganization,
    canManageOrganization,
    reloadPermissions: () => {}, // Permissions are now managed by SessionContext
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};